import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { locations as locApi } from '@/api/endpoints';
import { getApiErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the newly created home right before the dialog closes. */
  onCreated: (home: { id: number }) => void;
}

/**
 * Minimal "create a new home" dialog opened from the trailing "+ Yeni Ev" card
 * (and the zero-homes fallback button) in HomeCardStrip — see issue #104's
 * approved design (Option A). A name is the only thing asked for: type is
 * implicitly HOME and parent is implicitly none (root), with no picker for
 * either, unlike LocationQuickCreateDialog (used from the item form), which
 * still lets the user choose from the full set of non-HOME types for a root
 * location. This is deliberately the *only* UI in the app that can create a
 * HOME / a parentless location — the generic location form's "Üst Konum"
 * select no longer offers an empty/"(Kök)" option, and its type picker no
 * longer offers HOME.
 */
export function NewHomeDialog({ open, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setError(null);
  };

  const create = useMutation({
    mutationFn: () => locApi.create({ name: name.trim(), type: 'HOME', description: null, parentId: null }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['locations'] });
      // Dashboard/Items empty-state CTAs key off totalLocations from ['stats'],
      // which otherwise won't refresh until its own staleTime elapses — same
      // reasoning as the other location-creation mutations in this app.
      void qc.invalidateQueries({ queryKey: ['stats'] });
      onCreated({ id: created.id });
      onOpenChange(false);
      reset();
    },
    onError: (err) => {
      setError(getApiErrorMessage(err, t('errors.generic')));
    },
  });

  // Only fires for user-driven close interactions (Cancel, Escape) — the
  // dialog has no Trigger of its own, and `open` is otherwise fully controlled
  // by the parent — so it's safe to always reset here, keeping the form from
  // showing stale input the next time it's opened.
  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    reset();
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || create.isPending) return;
    setError(null);
    create.mutate();
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('locations.newHomeDialogTitle')}</AlertDialogTitle>
        </AlertDialogHeader>
        <form onSubmit={onSubmit} className="space-y-3" aria-label="new-home-form">
          <div className="space-y-1">
            <Label htmlFor="new-home-name">{t('locations.name')}</Label>
            <Input
              id="new-home-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </div>
          {error && <div role="alert" className="text-sm text-destructive">{error}</div>}
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </AlertDialogCancel>
            <Button type="submit" loading={create.isPending} disabled={!name.trim() || create.isPending}>
              {t('common.create')}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
